import React, { useEffect, useRef, useState } from 'react'
import { t } from 'ttag'
import { Box, Checkbox, FormControlLabel, Typography } from '@material-ui/core'
import YumButton from '../../components/YumButton/YumButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import { UserCompanyDto } from '../../services/api.types'
import { ImportResult } from './store/ImportUsersAdminReducer'
import { ReactComponent as UploadIcon } from '../../assets/v3/icons/announcements/upload.svg'

export interface IImportUsersAdminProps {
  managedCompanies: UserCompanyDto[]
  uploading: boolean
  result: ImportResult | null
  selectedCompanyId: string | null
}

export interface IImportUsersAdminDispatchProps {
  importUsers: (payload: { file: File; companyId: string; updateIfExists: boolean }) => void
  clearImportUsersResult: () => void
  setSelectedCompanyId: (payload: string) => void
}

type Props = IImportUsersAdminProps & IImportUsersAdminDispatchProps

export default function ImportUsersAdmin(props: Props) {
  const {
    managedCompanies,
    uploading,
    result,
    selectedCompanyId,
    importUsers,
    clearImportUsersResult,
    setSelectedCompanyId,
  } = props

  const [file, setFile] = useState<File | null>(null)
  const [updateIfExists, setUpdateIfExists] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setSelectedCompanyId(companyId)
    }
  }, [])

  useEffect(() => {
    setFile(null)
    clearImportUsersResult()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [companyId])

  // Clear file input after upload completes
  useEffect(() => {
    if (!uploading && result) {
      setFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [uploading, result])

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedCompanyId(event.target.value as string)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setFile(files[0])
      clearImportUsersResult()
    }
  }

  const handleSubmit = () => {
    if (!file || !companyId || uploading) return
    importUsers({ file, companyId, updateIfExists })
  }

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>{t`Import users`}</h1>
        {managedCompanies.length > 1 && (
          <YumSmallSelect variant="outlined" value={companyId} onChange={handleCompanyChange}>
            {managedCompanies.map((uc: UserCompanyDto, i: number) => (
              <option key={i} value={uc.companyId}>
                {uc.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>

      <Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
        {t`Import users who are unable to use the app themselves (e.g. no smartphone). Users who can use the app should register normally using a sign-up code. Upload an Excel file (.xlsx) with one user per row. Maximum 1000 rows per import.`}
      </Typography>

      <Typography variant="body2" style={{ marginBottom: 4 }}>
        <strong>{t`Columns:`}</strong>
      </Typography>
      <ul style={{ margin: '0 0 8px 0', paddingLeft: 24 }}>
        <li>email</li>
        <li>first_name</li>
        <li>last_name</li>
        <li>time_zone ({t`optional`})</li>
        <li>country ({t`optional`})</li>
        <li>team_name ({t`optional`})</li>
      </ul>

      <Typography variant="body2" style={{ marginBottom: 24 }}>
        <a href="/samples/import-users-sample.xlsx" download style={{ color: '#1976d2' }}>
          {t`Download sample file`}
        </a>
      </Typography>

      <Box style={{ marginBottom: 16 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
          disabled={uploading}
          id="import-users-file"
          style={{ display: 'none' }}
        />
        <label htmlFor="import-users-file" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <YumButton variant="contained" color="primary" component="span" size="small" disabled={uploading}>
            <UploadIcon style={{ marginTop: '-2px', marginRight: '8px' }} />
            {t`Choose file`}
          </YumButton>
          {file && (
            <Typography variant="body2" style={{ color: '#666' }}>
              {file.name}
            </Typography>
          )}
        </label>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={updateIfExists}
            onChange={(e) => setUpdateIfExists(e.target.checked)}
            color="primary"
            disabled={uploading}
          />
        }
        label={t`Update data, if user already exists`}
        style={{ marginBottom: 16, display: 'block' }}
      />

      <Box>
        <YumSubmitButton
          text={t`Import`}
          color="primary"
          variant="contained"
          disabled={!file || uploading}
          submitInProgress={uploading}
          onClick={handleSubmit}
        />
      </Box>

      {result && (
        <Box
          style={{
            marginTop: 24,
            padding: 16,
            backgroundColor: '#f5f5f5',
            borderRadius: 8,
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
            {t`Import results`}
          </Typography>
          <Typography variant="body2">
            {t`Total rows`}: {result.totalRows}
          </Typography>
          <Typography variant="body2">
            {t`Created`}: {result.created}
          </Typography>
          <Typography variant="body2">
            {t`Updated`}: {result.updated}
          </Typography>
          <Typography variant="body2">
            {t`Skipped`}: {result.skipped}
          </Typography>
          {result.errors && result.errors.length > 0 && (
            <Box style={{ marginTop: 12 }}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#d32f2f', marginBottom: 4 }}>
                {t`Errors`} ({result.errors.length}):
              </Typography>
              <Box style={{ maxHeight: 200, overflow: 'auto' }}>
                {result.errors.map((err, idx) => (
                  <Typography key={idx} variant="body2" style={{ color: '#d32f2f', fontSize: 13 }}>
                    {t`Row`} {err.row}: {err.message}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Card>
  )
}
